"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useLocale, useTranslations } from "next-intl";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";
import { normalizeUserLanguage } from "@/i18n/localeUtils";
import { useSession } from "@/lib/client/auth/client";
import { useLoadingStore } from "@/lib/client/stores/useLoadingStore";
import { fetcher } from "@/lib/http/fetcher";
import { Languages } from "@/lib/shared/constants";
import { GenderEnum } from "@/lib/shared/types/GenderEnum";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  HStack,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
} from "@/ui";

// define form schema
export const formSchema = z.object({
  email: z.string().email().min(1).max(50),
  name: z.string().optional(),
  gender: z.nativeEnum(GenderEnum).optional(),
  age: z.string().optional(),
  phone: z.string().optional(),
  language: z.string(),
});

export const ProfileForm = () => {
  const generalT = useTranslations("general");
  const validationT = useTranslations("validation");
  const profileT = useTranslations("profile");
  const locale = useLocale();
  const { data: session } = useSession();
  const sessionUser = session?.user as
    | {
        id: string;
        email?: string | null;
        name?: string | null;
        age?: number | null;
        gender?: string | null;
        phone?: string | null;
        language?: string | null;
      }
    | undefined;
  const { setIsLoading } = useLoadingStore();

  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;

  useEffect(() => {
    reset({
      email: sessionUser?.email ?? "",
      name: sessionUser?.name || undefined,
      age: sessionUser?.age?.toString(),
      gender: sessionUser?.gender as GenderEnum | undefined,
      phone: sessionUser?.phone || undefined,
      language: normalizeUserLanguage(sessionUser?.language, locale),
    });
  }, [sessionUser, locale, reset]);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setIsLoading(true);

    try {
      const userData = {
        ...data,
        age: data.age ? Number(data.age) : undefined,
      };

      await fetcher("/api/users/me", {
        method: "PATCH",
        body: JSON.stringify(userData),
      });
      toast.success(profileT("profile.update_success") || "Profile updated successfully");
      reset();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update profile");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className={""}>
      <CardContent className="">
        <CardHeader>
          <CardTitle> {profileT("profile.title")}</CardTitle>
          <CardDescription>{profileT("profile.description")}</CardDescription>
        </CardHeader>
        <Form {...form}>
          <form
            className="grid grid-cols-2 gap-2 lg:gap-4"
            onSubmit={handleSubmit(onSubmit)}
          >
            {/* Shipping information */}
            <FormField
              control={control}
              name="email"
              render={({ field }) => (
                <FormItem className={"col-span-2 sm:col-span-1"}>
                  <FormControl>
                    <Input
                      placeholder={profileT("profile.email")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage>
                    {errors.email && validationT("required", { field: "email" })}
                  </FormMessage>
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="gender"
              render={({ field }) => (
                <FormItem className={"col-span-2 sm:col-span-1"}>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value ? field.value : undefined}
                  >
                    <FormControl>
                      <SelectTrigger className={"w-full"}>
                        <SelectValue placeholder={profileT("profile.gender")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {[GenderEnum.Male, GenderEnum.Female, GenderEnum.Private].map((gen) => (
                        <SelectItem
                          key={gen}
                          value={gen}
                        >
                          {generalT(gen)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="name"
              render={({ field }) => (
                <FormItem className={"sm:col-span-1"}>
                  <FormControl>
                    <Input
                      placeholder={profileT("profile.name")}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="age"
              render={({ field }) => (
                <FormItem className={"sm:col-span-1"}>
                  <FormControl>
                    <Input
                      type={"number"}
                      min={0}
                      placeholder={profileT("profile.age")}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="phone"
              render={({ field }) => (
                <FormItem className={"sm:col-span-1"}>
                  <FormControl>
                    <Input
                      placeholder={profileT("profile.phone")}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="language"
              render={({ field }) => (
                <FormItem className={"sm:col-span-1"}>
                  <FormControl>
                    <HStack className={"items-center space-x-2"}>
                      <Text className={"flex-1"}>{profileT("profile.language")}</Text>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value ? field.value : undefined}
                      >
                        <FormControl>
                          <SelectTrigger className={"w-2/5"}>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.entries(Languages).map(([key, value]) => (
                            <SelectItem
                              key={key}
                              value={value}
                            >
                              {key}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </HStack>
                  </FormControl>
                </FormItem>
              )}
            />
            <CardFooter className={"col-span-2 p-0"}>
              <Button
                type="submit"
                className={"w-full"}
                disabled={!(sessionUser && form.formState.isDirty)}
              >
                {generalT("submit")}
              </Button>
            </CardFooter>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
